import { useState } from "react";
import { BellRing, X } from "lucide-react";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { mailbox, mailErrorMessage } from "../../api/mailbox";

// 待回覆提醒(規格 §2.6):「提醒我回覆」→ 明天 / 3 天 / 1 週。到期仍沒回覆(同串沒有本人寄出的信),
// 後端用既有通知提醒一次。已設定的顯示到期日、可取消。
//
// 讀信的回應不帶提醒狀態,所以「已設定」是頁面記住的這一次工作階段裡設的那些(`due`)。

const CHOICES: { days: 1 | 3 | 7; label: string }[] = [
  { days: 1, label: "明天" },
  { days: 3, label: "3 天後" },
  { days: 7, label: "1 週後" },
];

export interface FollowupControlProps {
  messageId: number;
  /** 已設定的到期時間(ISO);沒有是 null。 */
  due: string | null;
  onChange: (due: string | null) => void;
}

export function FollowupControl({ messageId, due, onChange }: FollowupControlProps) {
  const toast = useToast();
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = async (days: 1 | 3 | 7) => {
    setBusy(true);
    try {
      const got = await mailbox.setFollowup(messageId, days);
      setChoosing(false);
      onChange(got?.due_at ?? null);
      toast.push("到時候還沒回覆會提醒你", "success");
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await mailbox.clearFollowup(messageId);
      onChange(null);
      toast.push("已取消提醒", "success");
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  if (due) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5 text-sm text-[var(--text-2)]">
        <BellRing aria-hidden="true" size={14} />
        <span>
          會在 <span className="tabular">{formatTaipeiDateTime(due)}</span> 提醒你回覆
        </span>
        <Button size="sm" variant="ghost" loading={busy} icon={<X aria-hidden="true" size={14} />} onClick={() => void clear()}>
          取消提醒
        </Button>
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Button
        size="sm"
        aria-expanded={choosing}
        icon={<BellRing aria-hidden="true" size={16} />}
        onClick={() => setChoosing((v) => !v)}
      >
        提醒我回覆
      </Button>
      {choosing ? (
        <span role="group" aria-label="多久之後提醒" className="inline-flex gap-1">
          {CHOICES.map((choice) => (
            <Button key={choice.days} size="sm" variant="ghost" disabled={busy} onClick={() => void set(choice.days)}>
              {choice.label}
            </Button>
          ))}
        </span>
      ) : null}
    </span>
  );
}
